import { useState, useRef } from "react"; // useRef add kiya file input reset ke liye

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import MDButton from "components/MDButton";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Local components
import SimpleOpenLayersMap from "./cachementMap";
import VehicleList from "./VehicleList";
import OptimizedRouteMap from "./optimizedRouteMap";

import * as XLSX from "xlsx";

function RouteOptimization() {
  const [loading, setLoading] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [selectedJourneys, setSelectedJourneys] = useState({});
  const [excelStops, setExcelStops] = useState([]);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [showOptimizedMap, setShowOptimizedMap] = useState(false);

  // File input reset karne ke liye ref
  const fileInputRef = useRef(null);

  const handleJourneysChange = (newJourneys, newSelectedJourneys) => {
    setJourneys(newJourneys);
    setSelectedJourneys(newSelectedJourneys);
  };

  const downloadTemplate = () => {
    const templateData = [
      ["Bus Name", "Latitude", "Longitude", "Address", "Student", "Capacity"],
      ["BUS-001", 17.3850, 78.4867, "Hyderabad", 1, 50],
    ];
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bus Routes");
    XLSX.writeFile(wb, "BusRoutesTemplate.xlsx");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "array" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        const stops = data.map((row) => ({
          lat: parseFloat(row["Latitude"]),
          lng: parseFloat(row["Longitude"]),
          locationName: row["Address"] || "N/A",
          studentName: row["Student"] ? [row["Student"].toString()] : ["N/A"],
          busName: row["Bus Name"] || "Excel Load"
        })).filter(s => !isNaN(s.lat) && !isNaN(s.lng));

        setExcelStops(stops);
      } catch (err) {
        console.error("Excel Read Error:", err);
        alert("Error reading excel file.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Excel ko Remove karne ka logic
  const handleRemoveExcel = () => {
    setExcelStops([]); // State clear karein
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Input reset karein taaki same file dubara upload ho sake
    }
  };

  const fetchOptimizedRoutes = async () => {
    const selectedVehicleIds = [...new Set(
      journeys.filter(j => selectedJourneys[j.journeyId]).map(j => j.vehicleNo)
    )];

    const manualStops = journeys
      .filter((j) => selectedJourneys[j.journeyId])
      .flatMap((j) => j.stopPoints.map(p => ({
        lat: p.location.latitude,
        lng: p.location.longitude,
        locationName: p.locationName,
      })));

    const allStops = [...manualStops, ...excelStops];
    const totalVehicles = selectedVehicleIds.length > 0 ? selectedVehicleIds : ["Optimized Bus"];

    if (allStops.length < 2) {
      alert("At least 2 stops are required.");
      return;
    }

    setLoading(true);
    try {
      const routes = await Promise.all(totalVehicles.map(async (vNo, index) => {
        const vehicleStops = allStops.filter((_, i) => i % totalVehicles.length === index);
        if (vehicleStops.length < 2) return null;

        const waypoints = vehicleStops.map(s => `${s.lng},${s.lat}`).join(";");
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson`);
        const data = await res.json();

        return data.code === "Ok" ? {
          vehicleNo: vNo,
          routePath: data.routes[0].geometry.coordinates.map(c => ({ lat: c[1], lng: c[0] })),
          stops: vehicleStops,
        } : null;
      }));

      setOptimizedRoutes(routes.filter(r => r !== null));
      setShowOptimizedMap(true);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const isAnyJourneySelected = Object.values(selectedJourneys).some((val) => val === true);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>Controls</MDTypography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <MDButton variant="gradient" color="secondary" fullWidth onClick={downloadTemplate} startIcon={<Icon>download</Icon>} sx={{ fontSize: '9px' }}>Template</MDButton>
                </Grid>

                {/* Agar excel stops hain toh Remove button dikhao, warna Upload */}
                <Grid item xs={12} sm={4}>
                  {excelStops.length > 0 ? (
                    <MDButton variant="outlined" color="error" fullWidth onClick={handleRemoveExcel} startIcon={<Icon>delete</Icon>} sx={{ fontSize: '9px' }}>
                      Remove
                    </MDButton>
                  ) : (
                    <MDButton variant="outlined" color="success" component="label" fullWidth startIcon={<Icon>upload_file</Icon>} sx={{ fontSize: '9px' }}>
                      Upload <input type="file" hidden ref={fileInputRef} onChange={handleFileUpload} accept=".xlsx, .xls" />
                    </MDButton>
                  )}
                </Grid>

                <Grid item xs={12} sm={4}>
                  <MDButton variant="gradient" fullWidth disabled={loading || (!isAnyJourneySelected && excelStops.length === 0)} onClick={fetchOptimizedRoutes} sx={{ fontSize: '9px' }}
                    style={{ color: 'black', backgroundColor: '#FB8C00' }}
                  >
                    {loading ? <CircularProgress size={12} color="inherit" /> : "Optimize"}
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
            <MDBox sx={{ maxHeight: "65vh", overflowY: "auto" }}>
              <VehicleList onJourneysChange={handleJourneysChange} />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={8}>
            <MDBox mb={3} sx={{ height: 500, position: "relative", borderRadius: "12px", overflow: "hidden" }}>
              {showOptimizedMap ? (
                <>
                  <Button variant="contained" color="dark" size="small" sx={{ position: "absolute", zIndex: 10, top: 10, left: 10 }} onClick={() => setShowOptimizedMap(false)}>Back to Live Map</Button>
                  <OptimizedRouteMap routes={optimizedRoutes} />
                </>
              ) : (
                <SimpleOpenLayersMap journeys={journeys} selectedJourneys={selectedJourneys} excelStops={excelStops} />
              )}
            </MDBox>

            {(isAnyJourneySelected || excelStops.length > 0) && (
              <Card sx={{ p: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>Route Summary</MDTypography>
                <Divider />
                <MDBox sx={{ maxHeight: "400px", overflowY: "auto", mt: 1 }}>
                  {showOptimizedMap ? (
                    optimizedRoutes.map((r, i) => (
                      <MDBox key={i} mb={3}>
                        <MDTypography variant="button" fontWeight="bold" display="block">Route : {r.vehicleNo}</MDTypography>
                        <MDTypography variant="button" fontWeight="medium" display="block" ml={1}>Stops :</MDTypography>
                        {r.stops.map((s, idx) => <MDTypography key={idx} variant="caption" display="block" sx={{ ml: 3 }}>{idx + 1}: {s.locationName}</MDTypography>)}
                      </MDBox>
                    ))
                  ) : (
                    <>
                      {journeys.filter(j => selectedJourneys[j.journeyId]).map((journey) => (
                        <MDBox key={journey.journeyId} mb={2}>
                          <MDTypography variant="button" fontWeight="bold" display="block">
                            Route : {journey.journeyName} ({journey.interval.startTime}-{journey.interval.endTime})
                          </MDTypography>
                          <MDTypography variant="button" fontWeight="medium" display="block" ml={1}>Stops :</MDTypography>
                          {journey.stopPoints.map((stop, idx) => (
                            <MDTypography key={idx} variant="caption" display="block" sx={{ ml: 3 }}>{idx + 1}: {stop.locationName}</MDTypography>
                          ))}
                        </MDBox>
                      ))}

                      {excelStops.length > 0 && (
                        <MDBox mb={2}>
                          <MDTypography variant="button" fontWeight="bold" color="warning" display="block">
                            Route : Uploaded Excel Data
                          </MDTypography>
                          <MDTypography variant="button" fontWeight="medium" display="block" ml={1}>Stops :</MDTypography>
                          {excelStops.map((stop, idx) => (
                            <MDTypography key={idx} variant="caption" display="block" sx={{ ml: 3 }}>{idx + 1}: {stop.locationName}</MDTypography>
                          ))}
                        </MDBox>
                      )}
                    </>
                  )}
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default RouteOptimization;